<template>
  <section ref="returnTarget" tabindex="-1" :aria-label="t('title')" class="ncrm-pref-links">
    <div v-if="!embedded" class="ncrm-pref-links__title">{{ t("title") }}</div>
    <p class="text-body-2 text-medium-emphasis mb-3">{{ t("subtitle") }}</p>
    <v-skeleton-loader v-if="loading && !points.length" type="list-item-two-line@2" />
    <v-alert v-else-if="error" type="error">
      {{ error }}
      <template #append><v-btn variant="text" @click="load">{{ $t("ncrm.contacts.dataProtection.record.retry") }}</v-btn></template>
    </v-alert>
    <v-alert v-else-if="!points.length" type="info">{{ t("empty") }}</v-alert>
    <div v-for="point in points" v-else :key="point.id" class="ncrm-pref-links__point">
      <div class="ncrm-pref-links__head">
        <span class="ncrm-pref-links__email">{{ point.value }}</span>
        <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-link-plus" @click="openIssue(point, $event)">
          {{ t("generate") }}
        </v-btn>
      </div>
      <p v-if="!point.links.length" class="text-body-2 text-medium-emphasis">{{ t("noLinks") }}</p>
      <div v-for="link in point.links" :key="link.id" class="ncrm-pref-links__row">
        <v-chip size="x-small" :color="statusColor(link)">{{ t(`status${capital(status(link))}`) }}</v-chip>
        <span class="ncrm-pref-links__meta">
          {{ t("issued") }} {{ formatLinkDate(link.issuedAt) }} · {{ t("expires") }} {{ formatLinkDate(link.expiresAt) }}
          · {{ t("language") }} {{ String(link.locale).toUpperCase() }}
        </span>
        <v-btn v-if="status(link) === 'active'" size="small" variant="text" color="error" @click="askRevoke(link)">
          {{ t("revoke") }}
        </v-btn>
      </div>
      <v-btn v-if="point.nextCursor" size="small" variant="text" :loading="point.loadingMore" @click="loadMore(point)">
        {{ t("more") }}
      </v-btn>
    </div>

    <!-- Every dismissal (overlay, Escape, close, Done) goes through closeIssue so the one-time URL is cleared.
         Teleported to body like NcrmPrivacyInvitationAction so it sits above the record's side panels. -->
    <teleport to="body">
      <v-navigation-drawer :model-value="issueOpen" location="right" temporary :width="520" class="ncrm-pref-links-drawer"
        role="dialog" :aria-modal="issueOpen" aria-labelledby="ncrm-pref-links-drawer-title"
        @update:model-value="(open) => { if (!open) closeIssue(); }" @keydown.esc="closeIssue">
        <div v-if="issueOpen" class="pa-5" @keydown.tab="trapFocus">
          <div class="d-flex align-center mb-3">
            <h3 id="ncrm-pref-links-drawer-title" class="text-h6 flex-grow-1">{{ t("drawerTitle") }}</h3>
            <v-btn ref="close" icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="closeIssue" />
          </div>
          <p class="text-body-2 mb-4">{{ t("drawerIntro", { email: issuePoint?.value || "" }) }}</p>
          <template v-if="!issuedUrl">
            <v-select v-model="issueLocale" :items="locales" :label="t('language')"
              :menu-props="{ zIndex: 3220 }" />
            <v-alert v-if="issueError" :type="issueUncertain ? 'warning' : 'error'" class="mb-3" role="status">
              {{ issueError }}
            </v-alert>
            <v-btn color="primary" :loading="issuing" prepend-icon="mdi-link-plus" @click="issue">{{ t("create") }}</v-btn>
          </template>
          <template v-else>
            <!-- One-time secret: shown here only, copied by the operator, never stored by the page. -->
            <v-alert type="warning" class="mb-3">{{ t("onceWarning") }}</v-alert>
            <v-text-field :model-value="issuedUrl" :label="t('linkLabel')" readonly />
            <div class="d-flex ga-3">
              <v-btn color="primary" prepend-icon="mdi-content-copy" @click="copy">{{ copied ? t("copied") : t("copy") }}</v-btn>
              <v-btn variant="text" @click="closeIssue">{{ t("done") }}</v-btn>
            </div>
          </template>
        </div>
      </v-navigation-drawer>
    </teleport>

    <!-- Not dismissible while the DELETE is pending, so a failure is always shown inline. -->
    <v-dialog v-model="revokeOpen" max-width="440" :persistent="revoking">
      <v-card :title="t('revokeTitle')" :text="t('revokeText')">
        <v-alert v-if="revokeError" type="error" class="mx-4">{{ revokeError }}</v-alert>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="revoking" @click="revokeOpen = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="error" variant="tonal" :loading="revoking" @click="revoke">{{ t("revoke") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Above the registry host drawer (2390) and the issuance drawer (3210); Vuetify's snackbar default is 2000. -->
    <v-snackbar v-model="saved" color="success" :timeout="3500" :z-index="3300">{{ t("revoked") }}</v-snackbar>
    <v-snackbar v-model="uncertainNotice" color="warning" :timeout="8000" :z-index="3300">
      {{ t("issueUncertain") }}
    </v-snackbar>
  </section>
</template>

<script>
import { trapDrawerFocus } from "@/Utile/drawerFocusTrap.mjs";
import {
  PREFERENCE_LINK_LOCALES, LINK_STATUS_COLORS, formatLinkDate, issuePreferenceLink, linkStatus, listPreferenceLinks,
  listPreferencePoints, revokePreferenceLink,
} from "@/Utile/ncrmPreferenceLinks.mjs";

// GDPR S7.3b-2 — controlled recovery panel (Privacy manager only). Either a CRM record (NcrmPrivacyRecordTab: the
// record's email points via /points) or, S7.3b-3, one contact point from the Privacy registry (`contactPoint`).
// Multi-step exception to "close on success": the drawer stays open to show the one-time link (docs/design.md).
export default {
  name: "NcrmPrivacyPreferenceLinks",
  props: { subjectType: { type: String, default: "" }, subjectId: { type: [Number, String], default: null },
    contactPoint: { type: Object, default: null }, embedded: { type: Boolean, default: false } },
  data: () => ({ loading: false, error: "", points: [], locales: PREFERENCE_LINK_LOCALES, issueOpen: false,
    issuePoint: null, issueLocale: "ro", issuing: false, issueError: "", issuedUrl: "", copied: false,
    issueGeneration: 0, loadSequence: 0, issueUncertain: false, uncertainNotice: false, subjectGeneration: 0, issueTrigger: null,
    revokeOpen: false, revokeTarget: null, revoking: false, revokeError: "", saved: false }),
  computed: {
    subjectKey() { return this.contactPoint ? `point:${this.contactPoint.id}` : `${this.subjectType}:${this.subjectId}`; },
  },
  watch: { subjectKey: "resetForSubject" },
  mounted() { this.load(); },
  beforeUnmount() {
    // Leaving the record (route change, back) must also invalidate an issuance in flight: its late link is revoked.
    this.unmounted = true; this.issueGeneration += 1; this.subjectGeneration += 1; this.loadSequence += 1;
  },
  methods: {
    formatLinkDate,
    t(key, values) { return this.$t(`ncrm.contacts.dataProtection.record.preferenceLinks.${key}`, values); },
    capital: (value) => value.charAt(0).toUpperCase() + value.slice(1),
    status: (link) => linkStatus(link),
    statusColor(link) { return LINK_STATUS_COLORS[linkStatus(link)]; },
    async load() {
      // A newer load (other record or a reload) wins; stale completions never overwrite this record's data.
      const sequence = ++this.loadSequence;
      this.loading = true; this.error = "";
      try {
        const points = this.contactPoint ? [{ id: String(this.contactPoint.id), value: this.contactPoint.value }]
          : await listPreferencePoints({ subjectType: this.subjectType, subjectId: this.subjectId });
        const loaded = await Promise.all(points.map(async (point) => ({ ...point, loadingMore: false,
          ...(await listPreferenceLinks({ contactPointId: point.id }).then(({ items, nextCursor }) => ({ links: items, nextCursor }))) })));
        if (sequence === this.loadSequence) this.points = loaded;
      } catch { if (sequence === this.loadSequence) { this.points = []; this.error = this.t("loadError"); } }
      finally { if (sequence === this.loadSequence) this.loading = false; }
    },
    async loadMore(point) {
      const sequence = this.loadSequence; // a reload or another record makes this page stale
      point.loadingMore = true;
      try {
        const page = await listPreferenceLinks({ contactPointId: point.id, before: point.nextCursor });
        if (sequence === this.loadSequence) { point.links = point.links.concat(page.items); point.nextCursor = page.nextCursor; }
      } catch { if (sequence === this.loadSequence) this.error = this.t("loadError"); } finally { point.loadingMore = false; }
    },
    resetForSubject() {
      // Another record: nothing of the previous one (drawer, one-time URL, revoke target) may act on it.
      this.subjectGeneration += 1; this.issueGeneration += 1;
      Object.assign(this, { issueOpen: false, issuePoint: null, issuedUrl: "", copied: false, issuing: false, issueError: "",
        issueTrigger: null, revokeOpen: false, revokeTarget: null, revoking: false, revokeError: "", saved: false, points: [] });
      this.load();
    },
    openIssue(point, event) {
      const ui = String(this.$i18n?.locale || "").slice(0, 2);
      Object.assign(this, { issuePoint: point, issueLocale: ["ro", "en", "ru"].includes(ui) ? ui : "ro", issueError: "",
        issuedUrl: "", copied: false, issueOpen: true, issueTrigger: event?.currentTarget || null });
      this.$nextTick(() => this.$refs.close?.$el?.focus());
    },
    trapFocus: trapDrawerFocus,
    async issue() {
      const generation = this.issueGeneration, subject = this.subjectGeneration;
      this.issuing = true; this.issueError = ""; this.issueUncertain = false;
      try {
        const issued = await issuePreferenceLink({ contactPointId: this.issuePoint.id, locale: this.issueLocale });
        if (generation !== this.issueGeneration) {
          // The drawer was dismissed while issuing: nobody saw this link, so it must not stay active.
          await revokePreferenceLink(issued.id).catch(() => {});
          return this.unmounted ? undefined : this.load();
        }
        this.issuedUrl = issued.url;
      } catch (error) {
        // No response (or 5xx): the token may exist without its one-time URL. Reload so it shows up and can be revoked.
        const status = error?.response?.status, uncertain = !status || status >= 500;
        if (generation !== this.issueGeneration) {
          // Drawer already closed: still reconcile this record (not after unmount or a record switch) and warn.
          if (uncertain && !this.unmounted && subject === this.subjectGeneration) { this.uncertainNotice = true; this.load(); }
          return;
        }
        this.issueUncertain = uncertain;
        this.issueError = this.t(this.issueUncertain ? "issueUncertain" : "actionError");
        if (this.issueUncertain) await this.load(); // Generate stays disabled until the possible token is visible
      }
      finally { if (generation === this.issueGeneration) this.issuing = false; }
    },
    async copy() {
      try { await navigator.clipboard.writeText(this.issuedUrl); this.copied = true; } catch { this.copied = false; }
    },
    closeIssue() {
      const reload = Boolean(this.issuedUrl);
      this.issueGeneration += 1; // invalidates an issuance still in flight
      const trigger = this.issueTrigger;
      Object.assign(this, { issueOpen: false, issuedUrl: "", copied: false, issuing: false, issueTrigger: null });
      if (reload) this.load(); // the new link appears in the list; its URL is gone from memory
      this.$nextTick(() => (trigger?.isConnected ? trigger : this.$refs.returnTarget)?.focus());
    },
    askRevoke(link) { Object.assign(this, { revokeTarget: link, revokeError: "", revokeOpen: true }); },
    async revoke() {
      const subject = this.subjectGeneration;
      this.revoking = true; this.revokeError = "";
      try {
        await revokePreferenceLink(this.revokeTarget.id);
        if (subject === this.subjectGeneration) { Object.assign(this, { revokeOpen: false, saved: true }); await this.load(); }
      } catch { if (subject === this.subjectGeneration) this.revokeError = this.t("actionError"); }
      finally { if (subject === this.subjectGeneration) this.revoking = false; }
    },
  },
};
</script>

<style scoped>
.ncrm-pref-links:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 2px; }
.ncrm-pref-links__title { font-weight: 600; margin-bottom: 4px; color: var(--app-text-strong); }
.ncrm-pref-links__point { padding: 12px 0; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); }
.ncrm-pref-links__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.ncrm-pref-links__email { font-weight: 600; overflow-wrap: anywhere; }
.ncrm-pref-links__row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 6px 0; }
.ncrm-pref-links__meta { font-size: 0.85rem; opacity: 0.72; flex: 1 1 220px; }
</style>

<style>
/* Unscoped: the drawer and its scrim are teleported siblings. Both are fixed to the viewport (as in
   NcrmRecordEditDialog) so the dimmed backdrop covers the whole screen even when the record is scrolled.
   `+` targets only this drawer's own scrim: nested drawers teleport their scrims as later siblings too. */
.ncrm-pref-links-drawer.v-navigation-drawer {
  position: fixed !important; top: 0 !important; right: 0 !important; bottom: 0 !important; height: 100vh !important;
  max-width: 100vw; z-index: 3210 !important; text-align: left;
}
.ncrm-pref-links-drawer.v-navigation-drawer--active + .v-navigation-drawer__scrim {
  position: fixed !important; inset: 0 !important; z-index: 3209 !important;
}
</style>
